import React, { JSX, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';

// import { selectInspectionById } from '@/utils/inspection.utils';
import { ModuleCard } from './components/module-card/ModuleCard';
import * as Components from './components';
import { Footer } from './components/footer/footer';
import styles from './InspectionDetails.module.scss';
import { InspectionDefectChange } from '@/types/ModuleComponentProp.type';
import { useOpenInspection } from '@/hooks/useOpenInspection';
import { submitOpenedInspection } from '@/store/thunk/Inspection.thunk';
import { setPageHeader } from '@/store/slices/appDataSlice';
import { store } from '@/store/store';
import { useTemplate } from '@/hooks/useTemplate';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ROUTES } from '@/route/routes.constants';
import {
  closeInspection,
  updateOpenedInspection,
  clearOpenInspectionLoadError,
  addDefectWithChecklistSync,
  deleteDefectWithChecklistSync,
} from '@/store/slices/openInspectionSlice';
import { InspectionModule } from '@/types/Template.type';
import { SaveChangesModal } from './components/saveChangeCard/SaveChangesModal';
import { QuantityPrompt } from './components/modules/quantity-check/QuantityPrompt';
import { Preview } from './components/preview/Preview';
import { InspectionDefect } from '@/types/InspectionDefect';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { useNavigationContext } from '@/hooks/useNavigationContext';
import { useInspectionGpsCapture } from '@/hooks/useInspectionGpsCapture';
import { ActionButtons } from './ActionButtons';
import { LockedForUploadMessage } from './components/locked-for-upload-message/LockedForUploadMessage';
import { useCanOpenInspection } from '@/hooks/useCanOpenInspection';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { ROLE } from '@/role.constant';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { shouldSkipInspectionStartWorkflow } from '@/utils/inspectionStart.utils';
import { getChecklistModuleNames, isModuleChecked } from '@/utils/checked-modules.util';
import { validateInspectionChecklistSubmit } from '@/utils/inspection-checklist-validation.util';
import { getAvailableInspectionTemplate } from '@/utils/inspection.utils';
import { interpolate } from '@/utils/string.util';
import { useCheckpointChecks } from '@/hooks/masterData';
import { hasNestedCheckpointChecks, isCheckpointComplete } from '@/utils/checkpoint-checks.util';

export const InspectionDetails: React.FC = () => {
  const { t: translate } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { inspection: currentInspection, isModified } = useOpenInspection(id);
  const openedInspection = useAppSelector((state: RootState) => state.openedInspection.inspection);
  const isOpenedInspectionModified = useAppSelector(
    (state: RootState) => state.openedInspection.isModified
  );
  const cachedInspection = useAppSelector((state: RootState) =>
    state.inspectionsData.inspections.find((inspection) => inspection.id === id)
  );
  const currentUserId = useAppSelector((state: RootState) => state.auth.user?.id);
  const openLoadError = useAppSelector((state: RootState) => state.openedInspection.loadError);
  const sizeCharts = useAppSelector((state: RootState) => state.inspectionsData.sizeCharts);
  const template = useTemplate(currentInspection);
  const availableTemplate = useMemo(
    () =>
      getAvailableInspectionTemplate(template, {
        measurements: currentInspection?.measurements ?? [],
        sizeCharts,
        productId: currentInspection?.productId,
      }),
    [template, currentInspection?.measurements, currentInspection?.productId, sizeCharts]
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [emptyQuantityPromptOpen, setEmptyQuantityPromptOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [invalidChecklistModuleNames, setInvalidChecklistModuleNames] = useState<string[]>([]);
  const { setIsBlocked, setOnBlockedNavigation } = useNavigationContext();
  const canOpenInspection = useCanOpenInspection(id);
  const { hasRole } = useRoleAccess();
  const startInspectionCheck = useAppSelector(
    (state: RootState) => state.appConfig[APP_CONFIG_KEY.startInspectionCheck]
  );
  const mandatoryModuleCheck = useAppSelector((state: RootState) =>
    Boolean(state.appConfig[APP_CONFIG_KEY.mandatoryModuleCheck])
  );
  const { checkpointChecks } = useCheckpointChecks();

  useInspectionGpsCapture(id);

  const skipStartWorkFlowRef = useRef(!!location.state?.skipStartWorkFlow);

  useEffect(() => {
    store.dispatch(setPageHeader('Inspection'));
  }, []);

  /** Inspectors: show load failure on start page; admins: inline message below. */
  useLayoutEffect(() => {
    if (!id || !openLoadError || openLoadError.inspectionId !== id) {
      return;
    }
    if (hasRole(ROLE.ADMINISTRATOR)) {
      return;
    }
    dispatch(clearOpenInspectionLoadError());
    navigate(ROUTES.INSPECTION_START.replace(':id', id), {
      replace: true,
      state: { openInspectionHydrationError: openLoadError.reason },
    });
  }, [id, openLoadError, hasRole, navigate, dispatch]);

  // Ensure inspection is opened via the precheck page for non-administrator users.
  // skipStartWorkFlowRef captures ephemeral navigation state once on mount.
  // Durable skip checks (openedInspection / inspectionsData) survive tablet reloads.
  useEffect(() => {
    const shouldSkipStartWorkflow = shouldSkipInspectionStartWorkflow({
      skipStartWorkFlowFromNavigation: skipStartWorkFlowRef.current,
      inspectionId: id,
      currentUserId,
      openedInspection,
      cachedInspection,
      isOpenedInspectionModified,
    });
    if (shouldSkipStartWorkflow) {
      return;
    }
    const isAdministrator = hasRole(ROLE.ADMINISTRATOR);

    if (startInspectionCheck && !isAdministrator) {
      if (id) {
        navigate(ROUTES.INSPECTION_START.replace(':id', id), { replace: true });
      } else {
        navigate(ROUTES.INSPECTIONS, { replace: true });
      }
    }
  }, [
    id,
    navigate,
    hasRole,
    startInspectionCheck,
    currentUserId,
    openedInspection,
    cachedInspection,
    isOpenedInspectionModified,
  ]);

  // Update global navigation blocking state when local state changes
  useEffect(() => {
    setIsBlocked(isModified);
    setOnBlockedNavigation(() => setOpenDialog);
  }, [isModified, setIsBlocked, setOnBlockedNavigation]);

  // Clear invalid highlights once those modules become checked again
  useEffect(() => {
    setInvalidChecklistModuleNames((previous) => {
      if (previous.length === 0) {
        return previous;
      }
      const next = previous.filter((moduleName) => {
        if (
          hasNestedCheckpointChecks(checkpointChecks, currentInspection?.template?.id, moduleName)
        ) {
          return !isCheckpointComplete({
            checks: checkpointChecks,
            results: currentInspection?.checkpointCheckResults,
            templateId: currentInspection?.template?.id,
            checkpointCode: moduleName,
          });
        }
        return !isModuleChecked(currentInspection?.checkedModules, moduleName);
      });
      return next.length === previous.length ? previous : next;
    });
  }, [
    currentInspection?.checkedModules,
    currentInspection?.checkpointCheckResults,
    currentInspection?.template?.id,
    checkpointChecks,
  ]);

  // Block navigation when there are unsaved changes
  useNavigationBlocker({
    isBlocked: isModified,
    onBlockedNavigation: () => setOpenDialog(true),
    message: 'You have unsaved changes. Are you sure you want to leave?',
  });

  const checklistModuleNames = mandatoryModuleCheck
    ? getChecklistModuleNames(availableTemplate)
    : [];

  const handleSubmitClick = async () => {
    if (isSubmitting) return;
    const submitValidation = validateInspectionChecklistSubmit({
      template: availableTemplate,
      checkedModules: currentInspection?.checkedModules,
      isMandatoryModuleCheckEnabled: mandatoryModuleCheck,
      checkpointChecks,
      checkpointCheckResults: currentInspection?.checkpointCheckResults,
      templateId: currentInspection?.template?.id,
    });
    if (!submitValidation.isValid) {
      const uncheckedNames =
        submitValidation.uncheckedModules?.map((module) => module.moduleName) ?? [];
      setInvalidChecklistModuleNames(uncheckedNames);
      const uncheckedLabels = submitValidation.uncheckedModules
        ?.map((module) => translate(module.header))
        .join(', ');
      toast.error(
        uncheckedLabels
          ? interpolate(translate('inspection_checklist_incomplete_modules'), [uncheckedLabels])
          : translate(submitValidation.errorKey ?? 'inspection_checklist_incomplete')
      );
      return;
    }
    setInvalidChecklistModuleNames([]);
    if (
      currentInspection?.actualQuantityEnabled &&
      (currentInspection.actualQuantity == null || Number(currentInspection.actualQuantity) <= 0)
    ) {
      setEmptyQuantityPromptOpen(true);
      return;
    }
    await completeSubmit();
  };
  const completeSubmit = async () => {
    setEmptyQuantityPromptOpen(false);
    setIsSubmitting(true);

    try {
      if (isModified) {
        await store.dispatch(submitOpenedInspection({ id: currentInspection?.id }));
        toast.success(translate('Inspection_Submitted_Successfully'));
      } else {
        store.dispatch(closeInspection(currentInspection?.id || ''));
      }
      navigate(ROUTES.INSPECTIONS);
    } catch (error) {
      console.error('Error submitting inspection:', error);
      toast.error(translate('Inspection_Submission_Failed') || 'Failed to submit inspection');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePreviewClick = () => {
    setPreviewOpen(true);
  };
  const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { name, value, type } = event.target;

    let castedValue: string | number | boolean;

    // Determine the value type based on the input type
    switch (type) {
      case 'number':
        castedValue = parseFloat(value);
        if (isNaN(castedValue)) castedValue = 0; // Fallback for invalid numbers
        break;
      default:
        castedValue = value; // Defaults to string
    }
    store.dispatch(updateOpenedInspection({ id: currentInspection?.id, [name]: castedValue }));
  };
  const defectsHandler: InspectionDefectChange = {
    onAdd: (defect: InspectionDefect) => {
      const createdOn = new Date().toISOString();
      const newDefect: InspectionDefect = {
        ...defect,
        isNew: true,
        createdOn,
        defectId: defect?.defectCode?.id || '',
        inspectionDataId: currentInspection?.id || defect.inspectionDataId,
      };
      store.dispatch(
        addDefectWithChecklistSync({
          defect: newDefect,
          checklistModuleNames,
          checkpointChecks: checkpointChecks ?? [],
          checkpointCheckResults: currentInspection?.checkpointCheckResults,
          templateId: currentInspection?.template?.id,
        })
      );
    },
    onDelete: (defect: InspectionDefect) => {
      store.dispatch(
        deleteDefectWithChecklistSync({
          defect,
          checklistModuleNames,
          checkpointChecks: checkpointChecks ?? [],
          checkpointCheckResults: currentInspection?.checkpointCheckResults,
          templateId: currentInspection?.template?.id,
        })
      );
    },
    onEdit: (defect: InspectionDefect) => {
      defect.inspectionDataId = currentInspection?.id || defect.inspectionDataId;
      let filter: (d: InspectionDefect) => InspectionDefect = (d) =>
        d.id === defect.id ? defect : d;
      if (!defect.id) {
        filter = (d) => (d.newId === defect.newId ? defect : d);
      }
      store.dispatch(
        updateOpenedInspection({
          id: currentInspection?.id,
          qcDefects: [...currentInspection.qcDefects.map(filter)],
        })
      );
    },
  };

  const getComponent = (module: InspectionModule): JSX.Element => {
    const Component = Components[module.name as keyof typeof Components];
    return Component ? (
      <Component
        inspection={currentInspection}
        structure={module}
        onValueChange={onValueChange}
        defectsChangeHandlers={defectsHandler}
      />
    ) : (
      <div>Component `{module.name}` not found</div>
    );
  };

  const renderInspectionModule = (module: InspectionModule) => {
    switch (module.type) {
      case 'component':
        return getComponent(module);
      case 'module':
        return (
          <ModuleCard
            inspection={currentInspection}
            structure={module}
            defectsChangeHandlers={defectsHandler}
            isChecklistInvalid={invalidChecklistModuleNames.includes(module.name)}
          />
        );
      default:
        return <div>Unknown module type {module.type}</div>;
    }
  };

  const handleBackClick = () => {
    if (isModified) {
      setOpenDialog(true);
    } else {
      store.dispatch(closeInspection(currentInspection?.id || ''));
      navigate(ROUTES.INSPECTIONS);
    }
  };

  const handleDiscardChanges = () => {
    setOpenDialog(false);
    if (currentInspection) {
      store.dispatch(closeInspection(currentInspection?.id || ''));
    }
    navigate(ROUTES.INSPECTIONS);
  };
  const hasInvalidRemovedQuantity =
    typeof currentInspection?.removedQuantity === 'number' &&
    typeof currentInspection?.sampleSize === 'number' &&
    currentInspection.removedQuantity > currentInspection.sampleSize;

  const actionButtons: ActionButtons = {
    secondaryButton: {
      label: 'Back',
      onClick: handleBackClick,
    },
    previewButton: canOpenInspection
      ? {
          label: 'Preview',
          onClick: handlePreviewClick,
        }
      : undefined,
  };
  if (canOpenInspection) {
    actionButtons.primaryButton = {
      label: isSubmitting ? 'Submitting...' : 'Submit',
      styleName: 'submit',
      onClick: handleSubmitClick,
      isDisabled: isSubmitting || !isModified || hasInvalidRemovedQuantity,
    };
  }

  if (
    openLoadError &&
    openLoadError.inspectionId === id &&
    hasRole(ROLE.ADMINISTRATOR) &&
    (openLoadError.reason === 'offline_lite' || openLoadError.reason === 'fetch_failed')
  ) {
    const messageKey =
      openLoadError.reason === 'offline_lite'
        ? 'inspection_hydration_requires_connection'
        : 'inspection_start_failed';
    return (
      <section className={styles.scrollableContainer}>
        <div className={styles.loadErrorPanel} role="alert">
          <p>{translate(messageKey)}</p>
          <button
            type="button"
            onClick={() => {
              dispatch(clearOpenInspectionLoadError());
              navigate(ROUTES.INSPECTIONS, { replace: true });
            }}
          >
            {translate('Back')}
          </button>
        </div>
      </section>
    );
  }

  if (!template) return <p>{translate('Loading')}</p>;
  return (
    <section className={styles.scrollableContainer}>
      {!canOpenInspection && <LockedForUploadMessage />}
      {canOpenInspection && (
        <div className={styles.scrollableItem}>
          {availableTemplate?.modules?.map((module, index) => (
            <div key={index}>{renderInspectionModule(module)}</div>
          ))}
        </div>
      )}
      {openDialog && (
        <SaveChangesModal
          onClose={() => setOpenDialog(false)}
          onCancel={() => setOpenDialog(false)}
          onDiscard={handleDiscardChanges}
        />
      )}
      {previewOpen && (
        <Preview inspection={currentInspection} onClose={() => setPreviewOpen(false)} />
      )}
      {emptyQuantityPromptOpen && (
        <QuantityPrompt
          titleId="actual-quantity-empty-title"
          title={translate('Actual Quantity Not Entered')}
          message={interpolate(
            translate(
              'No actual quantity was recorded. The inspection result and sampling plan will be calculated using the booked advised quantity ({0}).'
            ),
            [currentInspection?.lotSize ?? '']
          )}
          actions={[
            {
              id: 'proceed-without-actual-quantity',
              label: translate('Proceed with Submission'),
              tone: 'primary',
              onClick: () => {
                void completeSubmit();
              },
            },
            {
              id: 'return-to-actual-quantity',
              label: translate('Go Back & Enter Quantity'),
              tone: 'secondary',
              onClick: () => {
                setEmptyQuantityPromptOpen(false);
                document.getElementById('actualQuantity')?.focus();
              },
            },
          ]}
        />
      )}
      <Footer {...actionButtons} />
    </section>
  );
};
